import { useCallback, useEffect, useState } from 'react';

const useActiveSection = (sections = []) => {
  const [activeSection, setActiveSection] = useState('');

  const updateActiveSection = useCallback(() => {
    if (!sections.length) return;

    const scrollPosition = window.scrollY;
    const windowHeight = window.innerHeight;

    // 헤더 높이를 고려한 offset 설정
    const headerOffset = 80;

    let currentSection = '';
    let maxVisibilityRatio = 0;

    // 각 섹션의 가시성 비율 계산
    for (const sectionId of sections) {
      const element = document.getElementById(sectionId);
      if (!element) continue;

      const rect = element.getBoundingClientRect();
      const elementTop = rect.top + scrollPosition;
      const elementHeight = rect.height;

      // 섹션이 화면에 보이는 영역 계산
      const visibleStart = Math.max(elementTop, scrollPosition + headerOffset);
      const visibleEnd = Math.min(
        elementTop + elementHeight,
        scrollPosition + windowHeight
      );
      const visibleHeight = Math.max(0, visibleEnd - visibleStart);

      // 가시성 비율 계산 (0~1)
      const visibilityRatio = visibleHeight / elementHeight;

      // 가장 많이 보이는 섹션을 현재 섹션으로 설정
      if (visibilityRatio > maxVisibilityRatio && visibilityRatio > 0.1) {
        maxVisibilityRatio = visibilityRatio;
        currentSection = sectionId;
      }
    }

    // 특별한 경우 처리: 페이지 최상단이나 특정 섹션 근처
    const firstSectionElement = document.getElementById(sections[0]);
    if (firstSectionElement) {
      const firstSectionTop =
        firstSectionElement.getBoundingClientRect().top + scrollPosition;
      // 첫 번째 섹션 위에 있을 때
      if (scrollPosition < firstSectionTop - headerOffset) {
        currentSection = sections[0];
      }
    }

    // 페이지 하단 근처에서는 마지막 섹션을 활성화
    const documentHeight = document.documentElement.scrollHeight;
    const scrollBottom = scrollPosition + windowHeight;
    if (documentHeight - scrollBottom < 100) {
      currentSection = sections[sections.length - 1];
    }

    if (currentSection !== activeSection) {
      setActiveSection(currentSection);

      // URL 해시 업데이트 (브라우저 히스토리에 추가하지 않음)
      if (currentSection) {
        window.history.replaceState(null, '', `#${currentSection}`);
      }
    }
  }, [sections, activeSection]);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          updateActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    // 초기 활성 섹션 설정
    updateActiveSection();

    // 스크롤 이벤트 리스너 등록
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 리사이즈 이벤트도 처리 (섹션 크기 변경 시)
    window.addEventListener('resize', updateActiveSection, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateActiveSection);
    };
  }, [updateActiveSection]);

  return activeSection;
};

export default useActiveSection;
